import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, Info } from 'lucide-react';

import { Alert, AlertDescription } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import {
    BULK_UPLOAD_STATUS,
    BulkUploadDropzone,
    BulkUploadPendingChanges,
    BulkUploadRowFilterSwitch,
    BulkUploadStatus,
    BulkUploadTable,
    useBulkUpload,
} from 'shared/components/bulkUpload';

const TEMPLATE_COLUMN_DEFS = [
    { key: 'ShipmentId', label: 'Shipment ID', required: true },
    { key: 'Notes', label: 'Notes', required: false },
];

const DEFAULT_COLUMN_DEFS = [
    { key: 'shipmentId', label: 'Shipment ID', required: true },
    { key: 'notes', label: 'Notes', required: false },
];

function BulkShrinkShipmentUpload({ adjustmentApi, facilityId, isOpen }) {
    const [selectedFile, setSelectedFile] = useState(null);
    const [persistSuccess, setPersistSuccess] = useState(false);
    const wasOpenRef = useRef(false);

    const api = useMemo(
        () => ({
            edit: (key, params, rowsToUpdate, rowsToDelete) =>
                adjustmentApi.editShipmentIdBulk(key, params, rowsToUpdate, rowsToDelete),
            fetch: (key, params, signal) => adjustmentApi.fetchShipmentIdBulk(key, params, signal),
            persist: (key) => adjustmentApi.persistShipmentIdBulk(key),
            upload: (file, params) => adjustmentApi.uploadShipmentIdBulk(file, params),
        }),
        [adjustmentApi],
    );

    const params = useMemo(
        () => ({
            facilityId,
        }),
        [facilityId],
    );

    const bulkUpload = useBulkUpload({ api, params });

    useEffect(() => {
        if (isOpen && !wasOpenRef.current) {
            setSelectedFile(null);
            setPersistSuccess(false);
            bulkUpload.reset();
        }
        wasOpenRef.current = isOpen;
    }, [isOpen]);

    const canUpload = Boolean(facilityId) && !!selectedFile;
    const hasData = bulkUpload.rows.length > 0;
    const errorCount = bulkUpload.validationErrors.length;

    const handleFileSelect = useCallback((file) => {
        setSelectedFile(file);
    }, []);

    const handleDownloadTemplate = useCallback(() => {
        const headers = TEMPLATE_COLUMN_DEFS.map((col) => `${col.label}${col.required ? '*' : ''}`).join(',');
        const exampleValues = ['123456789', 'Optional note'].join(',');
        const csvContent = `${headers}\n${exampleValues}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'bulk_shrink_shipment_upload_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, []);

    const handleUploadSelectedFile = useCallback(() => {
        if (selectedFile && facilityId) {
            bulkUpload.uploadFile(selectedFile);
        }
    }, [bulkUpload, facilityId, selectedFile]);

    const handlePersist = useCallback(async () => {
        try {
            const result = await bulkUpload.persist();
            const hasErrors = result?.persistResults?.errors?.length > 0;
            if (!hasErrors) {
                setPersistSuccess(true);
                setSelectedFile(null);
                bulkUpload.reset();
            }
        } catch {
            // Error is already captured in bulkUpload.persistError
        }
    }, [bulkUpload]);

    const handleStartNew = useCallback(() => {
        setPersistSuccess(false);
        setSelectedFile(null);
    }, []);

    const handleStartOver = useCallback(() => {
        setPersistSuccess(false);
        setSelectedFile(null);
        bulkUpload.reset();
    }, [bulkUpload]);

    return (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
            {!hasData && !persistSuccess && (
                <>
                    <div className="flex items-center justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                            <Download className="mr-2 h-4 w-4" />
                            Download Template
                        </Button>
                    </div>
                    <BulkUploadDropzone
                        onFileSelect={handleFileSelect}
                        disabled={[BULK_UPLOAD_STATUS.LOADING, BULK_UPLOAD_STATUS.UPLOADING].includes(
                            bulkUpload.status,
                        )}
                        isUploading={bulkUpload.status === BULK_UPLOAD_STATUS.UPLOADING}
                    />
                    <div className="flex items-center justify-between gap-3">
                        <Button
                            variant="default"
                            size="sm"
                            onClick={handleUploadSelectedFile}
                            disabled={
                                !canUpload ||
                                [BULK_UPLOAD_STATUS.LOADING, BULK_UPLOAD_STATUS.UPLOADING].includes(bulkUpload.status)
                            }
                        >
                            Submit
                        </Button>
                    </div>
                </>
            )}

            <BulkUploadStatus
                entityName="Shipment Shrink Request"
                fetchError={bulkUpload.fetchError}
                handleStartNew={handleStartNew}
                hasData={hasData}
                infoMessages={[]}
                persistError={bulkUpload.persistError}
                persistErrors={bulkUpload.persistErrors}
                persistSuccess={persistSuccess}
                status={bulkUpload.status}
                uploadError={bulkUpload.uploadError}
            />

            {hasData &&
                !persistSuccess &&
                !bulkUpload.hasPersistErrors &&
                ![BULK_UPLOAD_STATUS.SUBMITTING, BULK_UPLOAD_STATUS.PERSISTING].includes(bulkUpload.status) && (
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                {errorCount > 0 ? (
                                    <div className="text-sm text-destructive">
                                        {errorCount} validation error{errorCount !== 1 ? 's' : ''} found. Please fix
                                        errors before saving.
                                    </div>
                                ) : (
                                    <div className="text-sm text-green-700">No validation errors. Ready to save.</div>
                                )}
                            </div>
                            <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                    {bulkUpload.showIssuesFilterHint && (
                                        <Alert
                                            className="w-full max-w-md border-sky-200 bg-sky-50 py-2 pr-3 text-sm text-sky-950 sm:w-auto dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-50"
                                            role="status"
                                        >
                                            <Info aria-hidden className="text-sky-600 dark:text-sky-400" />
                                            <AlertDescription className="text-sky-900 dark:text-sky-100">
                                                Showing only rows with validation issues, warnings, pending edits, or
                                                rows marked for deletion. Turn off the switch to see all rows.
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                    {bulkUpload.showIssuesRowFilter && (
                                        <BulkUploadRowFilterSwitch
                                            checked={bulkUpload.showIssuesOnly}
                                            disabled={bulkUpload.rows.length === 0}
                                            id="bulk-shrink-shipments-show-issues-only"
                                            onCheckedChange={bulkUpload.setShowIssuesOnly}
                                        />
                                    )}
                                </div>
                                {bulkUpload.hasUnsavedChanges && (
                                    <>
                                        <Button variant="outline" size="sm" onClick={bulkUpload.clearPendingChanges}>
                                            Discard Changes
                                        </Button>
                                        <Button variant="default" size="sm" onClick={bulkUpload.submitChanges}>
                                            Submit Changes
                                        </Button>
                                    </>
                                )}
                                <Button variant="outline" size="sm" onClick={handleStartOver}>
                                    Start Over
                                </Button>
                                <Button
                                    variant="default"
                                    size="sm"
                                    onClick={handlePersist}
                                    disabled={!bulkUpload.canPersist}
                                >
                                    Save Shrink Requests
                                </Button>
                            </div>
                        </div>
                        <BulkUploadTable
                            columnDefs={DEFAULT_COLUMN_DEFS}
                            rows={bulkUpload.tableRows}
                            errorsByCell={bulkUpload.errorsByCell}
                            pendingEdits={bulkUpload.pendingEdits}
                            updateCell={bulkUpload.updateCell}
                            pendingDeletes={bulkUpload.pendingDeletes}
                            toggleRowDelete={bulkUpload.toggleRowDelete}
                        />
                        <BulkUploadPendingChanges
                            pendingDeletes={bulkUpload.pendingDeletes}
                            pendingEdits={bulkUpload.pendingEdits}
                            rowsLength={bulkUpload.rows.length}
                        />
                    </div>
                )}
        </div>
    );
}

export { BulkShrinkShipmentUpload };
